import type { Memory } from "../../../api/threads/memoriesApi";
import { defineStateMachine } from "../../../shared/state/stateMachine";

/**
 * A memory's wording where it is used (`MemoryWording`): shown, edited, or a draft the place
 * lost to the memory's review step (OW-07). A lost draft is never sent; it stays in view until
 * the person discards it, or until a save sent before the hand-over lands.
 */

export interface WordingDraft {
  readonly title: string;
  readonly note: string;
}

export type WordingEditState =
  | { readonly status: "shown" }
  | { readonly status: "editing"; readonly draft: WordingDraft }
  | { readonly status: "displaced"; readonly draft: WordingDraft };

export type WordingEditEvent =
  | { readonly type: "opened"; readonly draft: WordingDraft }
  | { readonly type: "changed"; readonly draft: WordingDraft }
  /** Cancelled, or a lost draft discarded. */
  | { readonly type: "closed" }
  /** Another place owns the wording now; `memory` is its wording as last read. */
  | { readonly type: "handedOver"; readonly memory: Memory }
  /** The draft's save went through, sent before or after a hand-over. */
  | { readonly type: "saved" };

const shown = (): WordingEditState => ({ status: "shown" });

export const transitionWordingEdit = defineStateMachine<WordingEditState, WordingEditEvent>("memory wording", {
  shown: { opened: (_state, event) => ({ status: "editing", draft: event.draft }), saved: shown },
  editing: {
    changed: (_state, event) => ({ status: "editing", draft: event.draft }),
    closed: shown,
    saved: shown,
    // A draft that changes nothing loses nothing.
    handedOver: (state, event) => (sameWording(state.draft, wordingDraftOf(event.memory)) ? shown() : { status: "displaced", draft: state.draft }),
  },
  displaced: { closed: shown, saved: shown },
});

export function wordingDraftOf(memory: Memory): WordingDraft {
  return { title: memory.title, note: memory.note ?? "" };
}

function sameWording(draft: WordingDraft, current: WordingDraft): boolean {
  return draft.title.trim() === current.title && draft.note.trim() === current.note;
}
